import axios from 'axios';
import { getAccessToken } from './provider';

/**
 * The Donor Portal's HTTP client.
 *
 * Three properties matter here, and all three are about keeping the two portals'
 * credentials apart:
 *
 * 1. It is its own axios instance, created with axios.create(). The admin app
 *    installs interceptors on the global axios default instance (see
 *    src/utils/interceptor.js), and those set withCredentials = true on every
 *    request. Instance interceptors are not shared, so nothing registered there
 *    applies here.
 *
 * 2. withCredentials is false. The browser never attaches cookies to a donor
 *    request, so an admin's auth_token cookie on the shared parent domain cannot
 *    ride along to a donor endpoint.
 *
 * 3. The donor token travels in an explicit Authorization header, set per
 *    request. Because it is never ambient, it cannot be sent by a cross-site
 *    form or image -- which is why these routes need no CSRF defence.
 *
 * Donor components must import this, never the bare `axios`.
 */

const baseURL =
    process.env.REACT_APP_DONOR_API_ENDPOINT || process.env.REACT_APP_API_ENDPOINT || '';

export const DonorApiError = {
    UNAUTHENTICATED: 'DONOR_UNAUTHENTICATED',
    NO_RECORDS: 'DONOR_NOT_ELIGIBLE',
    RATE_LIMITED: 'RATE_LIMITED',
    NETWORK: 'NETWORK_ERROR',
    UNKNOWN: 'INTERNAL_ERROR',
};

const donorApi = axios.create({
    baseURL,
    withCredentials: false,
    timeout: 20000,
    headers: { 'Content-Type': 'application/json' },
});

/**
 * Set by DonorAuthContext so the interceptor can hand control back to React on
 * a dead session, instead of doing a hard window.location redirect that would
 * lose the current route.
 */
let onSessionExpired = null;
export const setSessionExpiredHandler = (handler) => {
    onSessionExpired = handler;
};

donorApi.interceptors.request.use(async (request) => {
    // Fetched per request rather than cached: the provider refreshes an expired
    // token transparently, so asking each time is what keeps a long session
    // working without any expiry bookkeeping here.
    //
    // This is the ID token. The API identifies a donor by their verified email,
    // which a Cognito access token does not carry -- see hosted-ui.provider.js.
    const token = await getAccessToken();
    if (token) {
        request.headers.Authorization = `Bearer ${token}`;
    }
    // Belt and braces: even if a default is changed elsewhere, a donor request
    // must never carry cookies.
    request.withCredentials = false;
    return request;
});

donorApi.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error?.response?.status;
        const code = error?.response?.data?.error?.code;
        const message = error?.response?.data?.error?.message;

        if (!error.response) {
            return Promise.reject({
                code: DonorApiError.NETWORK,
                message: 'Could not reach the server. Please check your connection.',
            });
        }

        if (status === 401) {
            // The token is gone or no longer valid. There is deliberately no
            // retry here: the provider already refreshes on every request, so a
            // 401 means the refresh token is dead too.
            if (onSessionExpired) onSessionExpired();
            return Promise.reject({
                code: DonorApiError.UNAUTHENTICATED,
                message: 'Your session has ended. Please sign in again.',
            });
        }

        if (status === 403) {
            // Signed in correctly, but there are no donation records for this
            // address. Deliberately NOT treated as a session problem: sending
            // them to the login screen would have them sign in successfully and
            // bounce straight back here, forever.
            return Promise.reject({
                code: DonorApiError.NO_RECORDS,
                message:
                    message || 'We could not find donation records for your account.',
            });
        }

        if (status === 429) {
            return Promise.reject({
                code: DonorApiError.RATE_LIMITED,
                message: 'Too many requests. Please wait a moment and try again.',
            });
        }

        return Promise.reject({
            code: code || DonorApiError.UNKNOWN,
            message: message || 'Something went wrong. Please try again.',
        });
    },
);

/** Every Donor Portal endpoint. All reads; the API exposes nothing else. */
export const donorEndpoints = {
    profile: () => donorApi.get('donor-portal/me').then((r) => r.data),
    organizations: () => donorApi.get('donor-portal/organizations').then((r) => r.data),
    summary: (organizationId) =>
        donorApi
            .get('donor-portal/summary', { params: organizationId ? { organizationId } : {} })
            .then((r) => r.data),
    donations: (params) =>
        donorApi.get('donor-portal/donations', { params }).then((r) => r.data),
    /**
     * One donation, fetched only when the donor opens it. Everything in the
     * history row, plus:
     *
     *   designation    string | null
     *   paymentMethod  { wallet, brand, last4 } | null   (each string | null)
     *   comment        string | null
     *   responses      [{ question, answer }]
     *
     * The server looks the donation up by id AND the signed-in donor's email,
     * and answers 404 for anyone else's.
     */
    donation: (id) =>
        donorApi.get(`donor-portal/donations/${encodeURIComponent(id)}`).then((r) => r.data),
    /** The tax receipt for one successful donation, as a PDF Blob. */
    receipt: (id) =>
        donorApi
            .get(`donor-portal/donations/${encodeURIComponent(id)}/receipt`, {
                responseType: 'blob',
            })
            .then((r) => r.data),
    recurringPlans: (organizationId) =>
        donorApi
            .get('donor-portal/recurring-plans', {
                params: organizationId ? { organizationId } : {},
            })
            .then((r) => r.data),
};

export default donorApi;
